import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { Upload, X } from "lucide-react";
import { format } from "date-fns";
import { NotAvailableYet } from "@/components/NotAvailableYet";

interface EntityFormProps {
  entity?: any;
  onSuccess: () => void;
  onCancel: () => void;
}

const entitySchema = z.object({
  vehicle_name: z.string()
    .trim()
    .min(1, "Legal Name is required")
    .max(200, "Legal Name must be less than 200 characters"),
  vehicle_short_name: z.string()
    .trim()
    .max(100, "Vehicle Short Name must be less than 100 characters")
    .optional()
    .or(z.literal("")),
  created_on: z.string().min(1, "Created On date is required"),
  listing_documents: z.string()
    .trim()
    .max(500, "Listing Documents must be less than 500 characters")
    .optional()
    .or(z.literal("")),
});

export const EntityForm = ({ entity, onSuccess, onCancel }: EntityFormProps) => {
  const [formData, setFormData] = useState({
    vehicle_name: "",
    vehicle_short_name: "",
    created_on: "",
    listing_documents: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [existingFileUrls, setExistingFileUrls] = useState<string[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    if (entity) {
      setFormData({
        vehicle_name: entity.vehicle_name || "",
        vehicle_short_name: entity.vehicle_short_name || "",
        created_on: entity.created_on || "",
        listing_documents: entity.listing_documents || "",
      });
      
      if (entity.listing_documents_url) {
        try {
          const urls = JSON.parse(entity.listing_documents_url);
          setExistingFileUrls(Array.isArray(urls) ? urls : [entity.listing_documents_url]);
        } catch {
          setExistingFileUrls([entity.listing_documents_url]);
        }
      }
    }
  }, [entity]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setUploadedFiles(prev => [...prev, ...files]);
    }
  }, []);

  const handleRemoveNewFile = useCallback((index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleRemoveExistingFile = useCallback((index: number) => {
    setExistingFileUrls(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      setUploadedFiles(prev => [...prev, ...files]);
    }
  }, []);

  const uploadFiles = async (entityId: string): Promise<string[]> => {
    if (uploadedFiles.length === 0) return [];

    const uploadPromises = uploadedFiles.map(async (file, index) => {
      const fileExt = file.name.split('.').pop();
      const timestamp = Date.now();
      const fileName = `${entityId}_${timestamp}_${index}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('listing-documents')
        .upload(filePath, file, {
          upsert: true,
          contentType: file.type,
          cacheControl: '3600',
        });

      if (uploadError) {
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('listing-documents')
        .getPublicUrl(filePath);

      return publicUrl;
    });

    return Promise.all(uploadPromises);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      const validatedData = entitySchema.parse(formData);

      const dataToSubmit: any = {
        vehicle_name: validatedData.vehicle_name,
        vehicle_short_name: validatedData.vehicle_short_name || null,
        created_on: validatedData.created_on,
        listing_documents: validatedData.listing_documents || null,
      };

      if (entity) {
        // Update existing entity
        const { error } = await supabase
          .from("entities")
          .update(dataToSubmit)
          .eq("id", entity.id);
          
        if (error) throw error;
        toast({ title: "Entity updated successfully" });
      } else {
        // Create new entity
        const { error: insertError } = await supabase
          .from("entities")
          .insert(dataToSubmit)
          .select()
          .single();

        if (insertError) throw insertError;

        toast({ title: "Entity created successfully" });
      }

      onSuccess();
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(newErrors);
        toast({
          title: "Validation Error",
          description: "Please check the form fields",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <fieldset className="space-y-2">
        {/* FIG-324: Operating entities need an entity type column (UC-219). */}
        <legend className="text-sm font-medium">Entity Type</legend>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <label className="flex items-center gap-2">
            <input type="radio" name="entity_type" checked readOnly /> Vehicle (SPV)
          </label>
          <label className="flex items-center gap-2 text-muted-foreground">
            <input type="radio" name="entity_type" disabled /> Operating entity
            <NotAvailableYet useCase="UC-219" className="text-xs" />
          </label>
        </div>
      </fieldset>
      <div className="space-y-2">
        <Label htmlFor="vehicle_name">
          Legal Name <span className="text-destructive">*</span>
        </Label>
        <Input
          id="vehicle_name"
          placeholder="Enter Legal Name"
          value={formData.vehicle_name}
          onChange={(e) =>
            setFormData({ ...formData, vehicle_name: e.target.value })
          }
          className={errors.vehicle_name ? "border-destructive" : ""}
        />
        {errors.vehicle_name && (
          <p className="text-sm text-destructive">{errors.vehicle_name}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="vehicle_short_name">Vehicle Short Name</Label>
        <Input
          id="vehicle_short_name"
          placeholder="Enter Name Used Internally"
          value={formData.vehicle_short_name}
          onChange={(e) =>
            setFormData({ ...formData, vehicle_short_name: e.target.value })
          }
          className={errors.vehicle_short_name ? "border-destructive" : ""}
        />
        {errors.vehicle_short_name && (
          <p className="text-sm text-destructive">{errors.vehicle_short_name}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="created_on">
          Created On <span className="text-destructive">*</span>
        </Label>
        <Input
          id="created_on"
          type="date"
          placeholder="DD/MM/YYYY"
          value={formData.created_on}
          onChange={(e) =>
            setFormData({ ...formData, created_on: e.target.value })
          }
          className={errors.created_on ? "border-destructive" : ""}
        />
        {errors.created_on && (
          <p className="text-sm text-destructive">{errors.created_on}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="listing_documents_upload">
          Listing Documents (upload)
        </Label>
        
        <div className="space-y-2">
          {existingFileUrls.map((url, index) => (
            <div key={`existing-${index}`} className="flex items-center justify-between p-3 border rounded-md bg-muted">
              <span className="text-sm">{url.split('/').pop()}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveExistingFile(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          
          {uploadedFiles.map((file, index) => (
            <div key={`new-${index}`} className="flex items-center justify-between p-3 border rounded-md bg-primary/10">
              <span className="text-sm">{file.name}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveNewFile(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          
          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
            onClick={() => document.getElementById('file-upload')?.click()}
          >
            <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Drag and drop files here, or click to select multiple files
            </p>
            <Input
              id="file-upload"
              type="file"
              multiple
              className="hidden"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.xls,.xlsx"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? "Saving..." : entity ? "Update" : "Create"}
        </Button>
      </div>
    </form>
  );
};
